
import { useState } from "react";
import { toast } from "@/components/ui/use-toast";
import { FileItem, NewFileState } from "../types";
import { fileStorage } from "@/utils/fileStorage";
import { getCategoryLabel } from "../constants";

export const useNewFile = (files: FileItem[], setFiles: React.Dispatch<React.SetStateAction<FileItem[]>>) => {
  const [newFile, setNewFile] = useState<NewFileState>({
    name: "",
    type: "document",
    file: null,
    category: "",
    question: "",
    answer: "",
    duration: "",
    previewImage: null,
    title: "",
    description: ""
  });

  const handleNewFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setNewFile({ 
        ...newFile, 
        file: file,
        name: file.name 
      });
    }
  };

  const handleAddFile = () => {
    if (newFile.type !== "qa") {
      // Handle regular file upload
      if (!newFile.file) {
        toast({
          title: "Σφάλμα",
          description: "Παρακαλώ επιλέξτε ένα αρχείο",
          variant: "destructive"
        });
        return;
      }
      
      if (!newFile.title) {
        toast({
          title: "Σφάλμα",
          description: "Παρακαλώ εισάγετε έναν τίτλο",
          variant: "destructive"
        });
        return;
      }
    } else {
      // Handle Q&A entry
      if (!newFile.question || !newFile.answer) {
        toast({
          title: "Σφάλμα",
          description: "Παρακαλώ συμπληρώστε και την ερώτηση και την απάντηση",
          variant: "destructive"
        });
        return;
      }
    }
    
    if (!newFile.category) {
      toast({
        title: "Σφάλμα",
        description: "Παρακαλώ επιλέξτε μια κατηγορία",
        variant: "destructive"
      });
      return;
    }
    
    // For Q&A entries
    const fileSize = newFile.type === "qa" ? "1 KB" : (newFile.file!.size / (1024 * 1024)).toFixed(2) + " MB";
    const currentDate = new Date().toISOString().split('T')[0];
    
    // Create file URL for regular files or empty for Q&A
    const fileURL = newFile.type !== "qa" && newFile.file ? URL.createObjectURL(newFile.file) : "";
    
    const newFileObject: FileItem = {
      id: Date.now(),
      name: newFile.type === "qa" ? newFile.question!.substring(0, 50) : newFile.name,
      type: newFile.type,
      size: fileSize,
      uploadDate: currentDate,
      url: fileURL,
      category: newFile.category,
      fileData: newFile.file ?? undefined,
      title: newFile.title,
      description: newFile.description,
      duration: newFile.duration
    };
    
    // Add Q&A specific fields
    if (newFile.type === "qa") {
      newFileObject.question = newFile.question;
      newFileObject.answer = newFile.answer;
    }
    
    // Generate and add preview image URL if one was selected
    if (newFile.previewImage) {
      newFileObject.previewImage = newFile.previewImage;
    }
    
    // Save file to the database (localStorage)
    fileStorage.saveFile(newFileObject);
    
    // Add file to collection
    setFiles(prevFiles => [...prevFiles, newFileObject]);
    
    toast({
      title: "Επιτυχία",
      description: newFile.type === "qa" 
        ? `Η ερώτηση "${newFile.question}" προστέθηκε επιτυχώς στην κατηγορία "${getCategoryLabel(newFile.category)}"`
        : `Το αρχείο "${newFile.title || newFile.name}" προστέθηκε επιτυχώς στην κατηγορία "${getCategoryLabel(newFile.category)}"`
    });
    
    // Reset form
    setNewFile({
      name: "",
      type: "document",
      file: null,
      category: "",
      question: "",
      answer: "",
      duration: "",
      previewImage: null,
      title: "",
      description: ""
    });
  };

  return {
    newFile,
    setNewFile,
    handleNewFileChange,
    handleAddFile
  };
};
